import { useEffect, useState } from 'react'
import { apiService, type SheetPreviewHead } from '@/service/api'

/**
 * The first rows of a datasheet or timeseries file, for its card thumbnail.
 *
 * One request per file (GET v1/document/:id/preview-head), trimmed server-side
 * to what a card can show, and cached by DOCUMENT id for the session so panning,
 * zooming and re-renders never refetch. Keyed by document, never by sheet name:
 * every file's first sheet is "Sheet1", and a sheet-keyed cache would put one
 * file's rows on another file's card.
 *
 * A miss (no preview yet, an old upload, a network error) is remembered only
 * briefly, so a file that finishes processing gets its rows on a later mount.
 */

export const PREVIEW_HEAD_ROWS = 12
export const PREVIEW_HEAD_COLS = 6
const MISS_TTL_MS = 30_000

type Entry = { head: SheetPreviewHead | null; at: number }
const cache = new Map<string, Entry>()
const inflight = new Map<string, Promise<SheetPreviewHead | null>>()

function cached(docId: string): SheetPreviewHead | null | undefined {
  const e = cache.get(docId)
  if (!e) return undefined
  if (e.head === null && Date.now() - e.at > MISS_TTL_MS) {
    cache.delete(docId)
    return undefined
  }
  return e.head
}

function load(docId: string): Promise<SheetPreviewHead | null> {
  const pending = inflight.get(docId)
  if (pending) return pending
  // Started inside the chain so even a synchronous throw lands in .catch as a miss.
  const p = Promise.resolve()
    .then(() => apiService.getSheetPreviewHead(docId, { rows: PREVIEW_HEAD_ROWS, cols: PREVIEW_HEAD_COLS }))
    .then((head) => (head && Array.isArray(head.columns) && Array.isArray(head.rows) ? head : null))
    .catch(() => null)
    .then((head) => {
      cache.set(docId, { head, at: Date.now() })
      inflight.delete(docId)
      return head
    })
  inflight.set(docId, p)
  return p
}

export function clearSheetPreviewHeadCache(): void {
  cache.clear()
  inflight.clear()
}

/** `head` is null while loading and when the file has no preview; `loading` tells the two apart. */
export function useSheetPreviewHead(docId?: string): { head: SheetPreviewHead | null; loading: boolean } {
  const [state, setState] = useState(() => {
    const hit = docId ? cached(docId) : undefined
    return { docId, head: hit ?? null, loading: !!docId && hit === undefined }
  })

  useEffect(() => {
    if (!docId) {
      setState({ docId, head: null, loading: false })
      return
    }
    const hit = cached(docId)
    if (hit !== undefined) {
      setState({ docId, head: hit, loading: false })
      return
    }
    let live = true
    setState({ docId, head: null, loading: true })
    load(docId).then((head) => {
      if (live) setState({ docId, head, loading: false })
    })
    return () => {
      live = false
    }
  }, [docId])

  // A stale state from the previous docId must never paint on this one.
  if (state.docId !== docId) return { head: null, loading: !!docId }
  return { head: state.head, loading: state.loading }
}
